/**
 * Scene Data - 3D scene definitions for the game world
 * 
 * Defines the structure for 3D environments including:
 * - Scene metadata (id, type, parent relationships)
 * - Spawn points for different entry methods
 * - Entrances/exits to other scenes
 * - Interaction points
 * - Environment settings
 * 
 * This is data-driven - scenes are not hardcoded into components.
 */

// Scene types
export const SCENE_TYPES = {
  SETTLEMENT_EXTERIOR: 'settlement_exterior',
  BUILDING_INTERIOR: 'building_interior',
  WILDERNESS: 'wilderness',
  DUNGEON: 'dungeon',
};

// Interaction types
export const INTERACTION_TYPES = {
  DOOR: 'door',           // Enter/exit buildings
  NPC: 'npc',             // Talk to NPCs (future)
  ITEM: 'item',           // Pick up items (future)
  CONTAINER: 'container', // Open containers (future)
  SIGN: 'sign',           // Read signs
  SEAT: 'seat',           // Sit down (future)
};

// ============================================
// THORNHAVEN EXTERIOR - Settlement Scene
// ============================================
export const THORNHAVEN_EXTERIOR = {
  id: 'thornhaven-exterior',
  name: 'Thornhaven',
  type: SCENE_TYPES.SETTLEMENT_EXTERIOR,
  
  // World hierarchy
  realmId: 'midgard',
  regionId: 'central-plains',
  settlementId: 'thornhaven',
  
  // Environment settings
  environment: {
    skyColor: '#87CEEB',
    groundColor: '#4a3728',
    ambientLight: 0.6,
    sunIntensity: 1.0,
    sunPosition: [50, 100, 50],
    fogColor: '#b8c4ce',
    fogNear: 50,
    fogFar: 200,
  },
  
  // Terrain/ground
  terrain: {
    size: { width: 100, depth: 100 },
    texture: 'grass', // Placeholder
  },
  
  // Spawn points for different arrival methods
  spawnPoints: {
    default: { 
      position: { x: 0, y: 0, z: 30 }, 
      rotation: { x: 0, y: 0, z: 0 } 
    },
    fastTravel: { 
      position: { x: 0, y: 0, z: 25 }, 
      rotation: { x: 0, y: Math.PI, z: 0 } 
    },
    fromGoldenMeadHall: { 
      position: { x: 15, y: 0, z: 8 }, 
      rotation: { x: 0, y: Math.PI / 4, z: 0 } 
    },
    fromMarket: { 
      position: { x: -15, y: 0, z: 0 }, 
      rotation: { x: 0, y: -Math.PI / 4, z: 0 } 
    },
  },
  
  // Buildings in the scene
  buildings: [
    {
      id: 'golden-mead-hall-building',
      name: 'The Golden Mead Hall',
      locationId: 'thornhaven-tavern', // Links to location data
      targetSceneId: 'thornhaven-tavern-interior',
      position: { x: 10, y: 0, z: 0 },
      rotation: { x: 0, y: -Math.PI / 6, z: 0 },
      size: { width: 12, height: 8, depth: 16 },
      style: 'tavern',
      entranceOffset: { x: 0, y: 0, z: 8.5 }, // Relative to building position
    },
    {
      id: 'market-building',
      name: "Merchant's Row",
      locationId: 'thornhaven-market',
      targetSceneId: null, // Not yet implemented
      position: { x: -15, y: 0, z: 5 },
      rotation: { x: 0, y: Math.PI / 8, z: 0 },
      size: { width: 20, height: 6, depth: 10 },
      style: 'market',
      entranceOffset: { x: 0, y: 0, z: 5.5 },
    },
    {
      id: 'smithy-building',
      name: 'Iron Forge Smithy',
      locationId: 'thornhaven-smithy',
      targetSceneId: null, // Not yet implemented
      position: { x: 20, y: 0, z: -15 },
      rotation: { x: 0, y: Math.PI / 3, z: 0 },
      size: { width: 8, height: 6, depth: 10 },
      style: 'workshop',
      entranceOffset: { x: 0, y: 0, z: 5.5 },
    },
    {
      id: 'residential-1',
      name: 'Cottage',
      locationId: null, // Generic building
      targetSceneId: null,
      position: { x: -25, y: 0, z: -10 },
      rotation: { x: 0, y: 0, z: 0 },
      size: { width: 6, height: 5, depth: 8 },
      style: 'house',
      entranceOffset: { x: 0, y: 0, z: 4.5 },
    },
    {
      id: 'residential-2',
      name: 'House',
      locationId: null,
      targetSceneId: null,
      position: { x: -20, y: 0, z: -20 },
      rotation: { x: 0, y: Math.PI / 4, z: 0 },
      size: { width: 7, height: 5, depth: 9 },
      style: 'house',
      entranceOffset: { x: 0, y: 0, z: 5 },
    },
  ],
  
  // Paths/roads
  paths: [
    {
      id: 'main-road',
      points: [
        { x: 0, z: 50 },
        { x: 0, z: 0 },
        { x: 0, z: -40 },
      ],
      width: 4,
      type: 'road',
    },
    {
      id: 'tavern-path',
      points: [
        { x: 0, z: 5 },
        { x: 10, z: 8 },
      ],
      width: 2,
      type: 'path',
    },
    {
      id: 'market-path',
      points: [
        { x: 0, z: 5 },
        { x: -15, z: 10 },
      ],
      width: 2,
      type: 'path',
    },
  ],
  
  // Interactive points (doors, signs, etc.)
  interactions: [
    {
      id: 'tavern-entrance',
      type: INTERACTION_TYPES.DOOR,
      position: { x: 10, y: 0, z: 8.5 },
      radius: 3,
      prompt: 'Enter The Golden Mead Hall',
      action: {
        type: 'enterScene',
        targetSceneId: 'thornhaven-tavern-interior',
        spawnPoint: 'entrance',
      },
    },
    {
      id: 'town-square-sign',
      type: INTERACTION_TYPES.SIGN,
      position: { x: 0, y: 0, z: 20 },
      radius: 2,
      prompt: 'Read Sign',
      action: {
        type: 'showText',
        text: 'Welcome to Thornhaven\nCrossroads of Midgard',
      },
    },
  ],
  
  // Collision boundaries (simplified for prototype)
  bounds: {
    minX: -45,
    maxX: 45,
    minZ: -45,
    maxZ: 45,
  },
};

// ============================================
// GOLDEN MEAD HALL INTERIOR - Building Scene
// ============================================
export const THORNHAVEN_TAVERN_INTERIOR = {
  id: 'thornhaven-tavern-interior',
  name: 'The Golden Mead Hall',
  type: SCENE_TYPES.BUILDING_INTERIOR,
  
  // World hierarchy
  realmId: 'midgard',
  regionId: 'central-plains',
  settlementId: 'thornhaven',
  locationId: 'thornhaven-tavern',
  
  // Parent scene for exit
  parentSceneId: 'thornhaven-exterior',
  exitSpawnPoint: 'fromGoldenMeadHall',
  
  // Environment settings (indoor)
  environment: {
    skyColor: null, // No sky indoors
    ambientLight: 0.3,
    pointLights: [
      { position: [0, 4, 0], intensity: 0.8, color: '#ff9944' }, // Fireplace glow
      { position: [-5, 3, 3], intensity: 0.4, color: '#ffcc88' }, // Candles
      { position: [5, 3, -3], intensity: 0.4, color: '#ffcc88' },
    ],
  },
  
  // Room dimensions
  room: {
    width: 16,
    height: 6,
    depth: 20,
    wallColor: '#5c4033',
    floorColor: '#3d2817',
  },
  
  // Spawn points
  spawnPoints: {
    entrance: { 
      position: { x: 0, y: 0, z: 8 }, 
      rotation: { x: 0, y: Math.PI, z: 0 } 
    },
    fastTravel: { 
      position: { x: 0, y: 0, z: 0 }, 
      rotation: { x: 0, y: 0, z: 0 } 
    },
  },
  
  // Props/furniture
  props: [
    {
      id: 'fireplace',
      type: 'fireplace',
      position: { x: 0, y: 0, z: -8 },
      size: { width: 4, height: 3, depth: 2 },
    },
    {
      id: 'bar-counter',
      type: 'counter',
      position: { x: -5, y: 0, z: -5 },
      size: { width: 6, height: 1.2, depth: 1.5 },
    },
    {
      id: 'table-1',
      type: 'table',
      position: { x: 4, y: 0, z: 0 },
      size: { width: 2, height: 0.8, depth: 2 },
    },
    {
      id: 'table-2',
      type: 'table',
      position: { x: -3, y: 0, z: 3 },
      size: { width: 2, height: 0.8, depth: 2 },
    },
    {
      id: 'table-3',
      type: 'table',
      position: { x: 5, y: 0, z: -4 },
      size: { width: 2, height: 0.8, depth: 2 },
    },
  ],
  
  // Interactions
  interactions: [
    {
      id: 'tavern-exit',
      type: INTERACTION_TYPES.DOOR,
      position: { x: 0, y: 0, z: 9.5 },
      radius: 2,
      prompt: 'Exit to Thornhaven',
      action: {
        type: 'exitScene',
        targetSceneId: 'thornhaven-exterior',
        spawnPoint: 'fromGoldenMeadHall',
      },
    },
  ],
  
  // Room bounds
  bounds: {
    minX: -7.5,
    maxX: 7.5,
    minZ: -9.5,
    maxZ: 9.5,
  },
};

// ============================================
// SCENE REGISTRY - All available scenes
// ============================================
export const SCENES = {
  'thornhaven-exterior': THORNHAVEN_EXTERIOR,
  'thornhaven-tavern-interior': THORNHAVEN_TAVERN_INTERIOR,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get a scene by ID
 */
export const getScene = (sceneId) => SCENES[sceneId] || null;

/**
 * Get spawn point from a scene
 */
export const getSpawnPoint = (sceneId, spawnPointName = 'default') => {
  const scene = getScene(sceneId);
  if (!scene) return null;
  return scene.spawnPoints[spawnPointName] || scene.spawnPoints.default || scene.spawnPoints.entrance;
};

/**
 * Get building data by locationId
 */
export const getBuildingByLocationId = (sceneId, locationId) => {
  const scene = getScene(sceneId);
  if (!scene || !scene.buildings) return null;
  return scene.buildings.find(b => b.locationId === locationId);
};

/**
 * Get interaction at a position (within radius)
 */
export const getInteractionAtPosition = (sceneId, position, maxDistance = 3) => {
  const scene = getScene(sceneId);
  if (!scene || !scene.interactions) return null;
  
  for (const interaction of scene.interactions) {
    const dx = position.x - interaction.position.x;
    const dz = position.z - interaction.position.z;
    const distance = Math.sqrt(dx * dx + dz * dz);
    
    if (distance <= interaction.radius) {
      return interaction;
    }
  }
  return null;
};

/**
 * Check if a position is within scene bounds
 */
export const isWithinBounds = (sceneId, position) => {
  const scene = getScene(sceneId);
  if (!scene || !scene.bounds) return true;
  
  const { minX, maxX, minZ, maxZ } = scene.bounds;
  return position.x >= minX && position.x <= maxX && 
         position.z >= minZ && position.z <= maxZ;
};

/**
 * Clamp position to scene bounds
 */
export const clampToBounds = (sceneId, position) => {
  const scene = getScene(sceneId);
  if (!scene || !scene.bounds) return position;
  
  const { minX, maxX, minZ, maxZ } = scene.bounds;
  return {
    x: Math.max(minX, Math.min(maxX, position.x)),
    y: position.y,
    z: Math.max(minZ, Math.min(maxZ, position.z)),
  };
};

/**
 * Get fast travel destination data
 * Returns the scene and spawn point for fast traveling to a location
 */
export const getFastTravelDestination = (settlementId, locationId = null) => {
  // If locationId specified, travel to interior
  if (locationId) {
    // Find the interior scene for this location
    const interiorScene = Object.values(SCENES).find(
      s => s.locationId === locationId && s.type === SCENE_TYPES.BUILDING_INTERIOR
    );
    
    if (interiorScene) {
      return {
        sceneId: interiorScene.id,
        spawnPoint: interiorScene.spawnPoints.fastTravel || interiorScene.spawnPoints.entrance,
        locationId,
      };
    }
  }
  
  // Otherwise, travel to settlement exterior
  const exteriorScene = Object.values(SCENES).find(
    s => s.settlementId === settlementId && s.type === SCENE_TYPES.SETTLEMENT_EXTERIOR
  );
  
  if (exteriorScene) {
    return {
      sceneId: exteriorScene.id,
      spawnPoint: exteriorScene.spawnPoints.fastTravel || exteriorScene.spawnPoints.default,
      locationId: null,
    };
  }
  
  return null;
};
